import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { AuthInput, AuthPasswordInput, AuthButton } from "./AuthLayout";
import { resetPassword, forgotPassword } from "../api/auth";

export function ResetPasswordForm({ email, onSuccess }: { email: string, onSuccess: () => void }) {
    const { t } = useTranslation();

    const schema = z.object({
        password: z.string().min(6, t('auth.resetPassword.errors.passwordMin')),
        confirm_password: z.string().min(6, t('auth.resetPassword.errors.confirmPasswordMin')),
    }).refine((data) => data.password === data.confirm_password, {
        message: t('auth.resetPassword.errors.passwordMismatch'),
        path: ["confirm_password"],
    });

    type FormData = z.infer<typeof schema>;

    const { register, handleSubmit, formState: { errors }, setError } = useForm<FormData>({
        resolver: zodResolver(schema),
    });
    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [resendCooldown, setResendCooldown] = useState(120); // 120 seconds cooldown
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    // Countdown timer effect
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setInterval(() => {
                setResendCooldown((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [resendCooldown]);

    const onSubmit = async (data: FormData) => {
        if (!otp || otp.length !== 6) {
            setOtpError(t('auth.resetPassword.errors.invalidCode'));
            return;
        }
        setOtpError("");
        setLoading(true);
        try {
            await resetPassword({
                email,
                code: otp,
                new_password: data.password,
                confirm_password: data.confirm_password
            });
            onSuccess();
        } catch (e: any) {
            console.error(e);
            const errorMessage = e.response?.data?.error?.message || e.response?.data?.message;
            if (errorMessage === "Invalid code") {
                setOtpError(t('auth.resetPassword.errors.codeInvalid'));
            } else if (errorMessage === "Code expired") {
                setOtpError(t('auth.resetPassword.errors.codeExpired'));
            } else {
                setError("root", { message: errorMessage || t('auth.resetPassword.errors.resetFailed') });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        setResendSuccess(false);
        setOtpError("");
        setError("root", { message: "" });
        try {
            await forgotPassword(email);
            setResendCooldown(120); // Reset to 120 seconds
            setResendSuccess(true);
            // Hide success message after 3 seconds
            setTimeout(() => setResendSuccess(false), 3000);
        } catch (e: any) {
            setError("root", { message: e.response?.data?.message || t('auth.resetPassword.errors.resendFailed') });
        } finally {
            setResendLoading(false);
        }
    };

    // Format time as MM:SS
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col items-center">
            <h1 className="font-bold text-3xl text-[#338585] mb-2">{t('auth.resetPassword.title')}</h1>
            <p className="text-gray-500 text-center mb-6 text-sm">
                {t('auth.resetPassword.subtitle')} <span className="text-[#D85C57]">{email}</span><br />
                {t('auth.resetPassword.enterCode')}
            </p>

            {/* Error Alert - Prominently displayed at the top */}
            {(otpError || errors.root?.message) && (
                <div className="w-full mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">
                                {otpError || errors.root?.message}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Alert for Resend */}
            {resendSuccess && (
                <div className="w-full mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">
                                {t('auth.resetPassword.success')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-6">
                <OtpInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={6}
                    renderSeparator={<span className="w-3"></span>}
                    renderInput={(props) => <input {...props} className="!w-12 !h-12 text-2xl text-center rounded-lg border-none shadow-sm focus:ring-1 focus:ring-[#E86C31] outline-none text-gray-700 bg-white" />}
                    containerStyle={{ justifyContent: 'center' }}
                    inputType="tel"
                />
            </div>

            <AuthPasswordInput
                placeholder={t('auth.resetPassword.passwordPlaceholder')}
                {...register("password")}
                error={errors.password?.message}
            />
            <AuthPasswordInput
                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                {...register("confirm_password")}
                error={errors.confirm_password?.message}
            />

            <AuthButton disabled={loading}>
                {loading ? t('auth.resetPassword.updating') : t('auth.resetPassword.updateButton')}
            </AuthButton>

            <div className="mt-8 text-sm text-gray-600">
                {t('auth.resetPassword.didntReceiveCode')} {" "}
                <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resendLoading}
                    className={`${resendCooldown > 0 || resendLoading ? 'text-gray-400 cursor-not-allowed' : 'text-[#D85C57] hover:underline'}`}
                >
                    {resendLoading ? t('auth.resetPassword.sending') : t('auth.resetPassword.resendAgain')}
                </button>
            </div>
            {resendCooldown > 0 && (
                <div className="mt-2 text-xs text-gray-400">
                    {t('auth.resetPassword.requestNewCode')} {formatTime(resendCooldown)}
                </div>
            )}
        </form>
    );
}
